import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Typography,
  Divider,
  Space,
  Form,
  Input,
  Tag,
  Timeline,
  Modal,
  message,
  Alert,
  Spin,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  UserOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { formatCurrency } from "../../utils/Utils";
import RABillApis from "../../api/WorkOrderManagement/RABillApis";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface RABillLine {
  raBillLineId: number;
  workOrderLineId: number;
  taskTitle: string;
  contractQuantity: number;
  totalExecutedQty: number;
  totalBilledQty: number;
  billedQuantity: number;
  rate: number;
  lineSubtotal: number;
  gstPercentage: number;
  gstValue: number;
  lineTotal: number;
  workCategoryName: string;
  uomName: string;
}

const RABillDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "view"; // 'view', 'approval'

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [raBill, setRABill] = useState<any>(null);
  const [raBillLines, setRABillLines] = useState<RABillLine[]>([]);
  const [deductions, setDeductions] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "return">("approve");

  useEffect(() => {
    if (id) {
      loadRABillDetails();
    }
  }, [id, mode]);

  const loadRABillDetails = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response:any = await RABillApis.getRABillById(id);

      if (response.status && response.data) {
        const billData = response.data;

        // Map API response to component state
        const mappedBill = {
          id: billData.raBillId,
          raBillNumber: billData.raBillSequenceId,
          workOrderNumber: billData.workOrderNumber,
          contractor: billData.contractorName,
          status: billData.status,
          site: billData.siteName,
          project: billData.projectName,
          raPeriod: {
            from: billData.raPeriodFrom,
            to: billData.raPeriodTo
          },
          woTotalValue: billData.grossTotal,
          cumulativeWorkDone: 0, // Calculate if needed
          cumulativeWorkValue: billData.subtotal,
          previousRABillsValue: billData.subtotal - billData.grossTotal,
          currentBillValue: billData.grossTotal,
          balanceWorksForRABill: billData.balanceAmount,
          paymentStatus: billData.paymentStatus,
          paidAmount: billData.paidAmount,
          createdBy: billData.createdByName,
          createdOn: billData.createdAt,
          lastPaymentDate: null,
          paymentMode: null
        };

        setRABill(mappedBill);
        setRABillLines(billData.raBillLines || []);

        // Map deductions from the API response
        const mappedDeductions = [
          {
            id: 'retention',
            type: 'Retention Amount',
            amount: billData.retentionAmount,
            percentage: null
          },
          {
            id: 'tds',
            type: 'TDS',
            amount: billData.tdsAmount,
            percentage: billData.tdsRate
          },
          {
            id: 'mobilization',
            type: 'Mobilization Recovery',
            amount: billData.mobilizationRecovery,
            percentage: null
          },
          {
            id: 'onhold',
            type: 'On Hold Amount',
            amount: billData.onHoldAmount,
            percentage: null
          }
        ].filter(deduction => deduction.amount > 0);

        setDeductions(mappedDeductions);

        // Map totals
        const mappedTotals = {
          subtotalExclTax: billData.subtotal,
          gstTotal: billData.gstTotal,
          grossTotal: billData.grossTotal,
          totalDeductions: billData.retentionAmount + billData.tdsAmount + billData.mobilizationRecovery + billData.onHoldAmount,
          netPayable: billData.netPayable
        };

        setTotals(mappedTotals);
      } else {
        message.error("RA Bill not found");
      }
    } catch (error) {
      console.error("Error loading RA Bill details:", error);
      message.error("Failed to load RA Bill details");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Draft: "default",
      Submitted: "processing",
      Checked: "warning",
      Verified: "success",
      Approved: "success",
      Posted: "purple",
      Paid: "green",
      Returned: "error",
    };
    return statusColors[status] || "default";
  };

  const handleBackNavigation = () => {
    const backRoutes: { [key: string]: string } = {
      approval: "ra-bills-pending-approvals",
      view: "ra-bills-view-approved",
    };

    navigate(-1);
  };

  const openActionModal = (type: "approve" | "return") => {
    setActionType(type);
    setActionModalVisible(true);
    form.resetFields();
  };

  const handleActionSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (actionType === "approve") {
        // Approve logic
        await new Promise((resolve) => setTimeout(resolve, 1000));

        Swal.fire({
          icon: "success",
          title: "RA Bill Approved!",
          text: `RA Bill ${raBill?.raBillNumber} has been approved successfully`,
          confirmButtonText: "OK",
        }).then(() => {
          handleBackNavigation();
        });
      } else {
        // Return logic
        await new Promise((resolve) => setTimeout(resolve, 1000));

        Swal.fire({
          icon: "info",
          title: "RA Bill Returned",
          text: `RA Bill ${raBill?.raBillNumber} has been returned to contractor`,
          confirmButtonText: "OK",
        }).then(() => {
          handleBackNavigation();
        });
      }

      setActionModalVisible(false);
    } catch (error) {
      message.error(`Failed to ${actionType} RA Bill`);
    } finally {
      setLoading(false);
    }
  };

  // Work Lines Table Columns
  const workLinesColumns: TableColumnsType<RABillLine> = useMemo(
    () => [
      {
        title: "Work Line #",
        dataIndex: "workOrderLineId",
        key: "workOrderLineId",
        width: 120,
        fixed: "left",
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: "Description",
        key: "description",
        width: 250,
        render: (_, record) => (
          <div>
            <div className="fw-bold">{record.taskTitle}</div>
            <div className="text-muted small">{record.workCategoryName}</div>
          </div>
        ),
      },
      {
        title: "UOM",
        dataIndex: "uomName",
        key: "uomName",
        width: 80,
        align: "center",
      },
      {
        title: "Contract Qty",
        dataIndex: "contractQuantity",
        key: "contractQuantity",
        width: 120,
        align: "right",
        render: (qty) => qty?.toLocaleString() || 0,
      },
      {
        title: "Total Executed Qty",
        dataIndex: "totalExecutedQty",
        key: "totalExecutedQty",
        width: 130,
        align: "right",
        render: (qty) => (
          <Text type="success" strong>
            {qty?.toLocaleString() || 0}
          </Text>
        ),
      },
      {
        title: "Total Billed Qty",
        dataIndex: "totalBilledQty",
        key: "totalBilledQty",
        width: 120,
        align: "right",
        render: (qty) => <Text type="secondary">{qty?.toLocaleString() || 0}</Text>,
      },
      {
        title: "Available to Bill",
        key: "availableQuantity",
        width: 120,
        align: "right",
        render: (_, record) => {
          const available = (record.totalExecutedQty || 0) - (record.totalBilledQty || 0);
          return (
            <Text strong style={{ color: "#1890ff" }}>
              {available.toLocaleString()}
            </Text>
          );
        },
      },
      {
        title: mode === "approval" ? "Billed Qty (Submitted)" : "Billed Qty",
        dataIndex: "billedQuantity",
        key: "billedQuantity",
        width: 130,
        align: "right",
        render: (qty) => (
          <Text
            strong
            style={{ color: mode === "approval" ? "#1890ff" : "#52c41a" }}
          >
            {qty?.toLocaleString() || 0}
          </Text>
        ),
      },
      {
        title: "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 100,
        align: "right",
        render: (rate) => `₹${rate?.toLocaleString() || 0}`,
      },
      {
        title: "Line Subtotal",
        dataIndex: "lineSubtotal",
        key: "lineSubtotal",
        width: 130,
        align: "right",
        render: (amount) => <Text strong>{formatCurrency(amount || 0,"INR")}</Text>,
      },
      {
        title: "GST %",
        dataIndex: "gstPercentage",
        key: "gstPercentage",
        width: 80,
        align: "center",
        render: (pct) => `${pct || 0}%`,
      },
      {
        title: "GST Value",
        dataIndex: "gstValue",
        key: "gstValue",
        width: 120,
        align: "right",
        render: (amount) => (
          <Text type="warning">{formatCurrency(amount || 0,"INR")}</Text>
        ),
      },
      {
        title: "Line Total",
        dataIndex: "lineTotal",
        key: "lineTotal",
        width: 130,
        align: "right",
        render: (amount) => (
          <Text strong style={{ color: "#52c41a" }}>
            {formatCurrency(amount || 0,"INR")}
          </Text>
        ),
      },
    ],
    [mode]
  );

  if (loading && !raBill) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Spin size="large" />
          <div className="mt-3">Loading RA Bill details...</div>
        </div>
      </Content>
    );
  }

  if (!raBill) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Title level={4}>RA Bill not found</Title>
          <Button onClick={handleBackNavigation}>Go Back</Button>
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <Head title={`RA Bill Details - ${raBill.raBillNumber}`} />

      <div className="nk-content-inner">
        <div className="nk-content-body">
          {/* Header */}
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between g-3">
              <div className="nk-block-head-content">
                <Title level={3} className="nk-block-title">
                  {mode === "approval" ? "Review RA Bill" : "RA Bill Details"} -{" "}
                  {raBill.raBillNumber}
                </Title>
                <div className="nk-block-des text-soft">
                  <Space>
                    <span>WO: {raBill.workOrderNumber}</span>
                    <Divider type="vertical" />
                    <span>Contractor: {raBill.contractor}</span>
                    <Divider type="vertical" />
                    <Tag color={getStatusColor(raBill.status)}>
                      {raBill.status}
                    </Tag>
                  </Space>
                </div>
              </div>
              <div className="nk-block-head-content">
                <Space>
                  {/* <Button icon={<EyeOutlined />}>Preview PDF</Button>
                  <Button icon={<DownloadOutlined />}>Export</Button> */}
                  {mode === "approval" && (
                    <>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => openActionModal("approve")}
                      >
                        Approve
                      </Button>
                      <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => openActionModal("return")}
                      >
                        Return
                      </Button>
                    </>
                  )}
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBackNavigation}
                    style={{ marginRight: 16 }}
                  >
                    Back
                  </Button>
                </Space>
              </div>
            </div>
          </div>

          {/* Header Summary */}
          <Card title="RA Bill Header Details" className="mb-4">
            <Row gutter={[24, 16]}>
              <Col xs={24} lg={8}>
                <div className="border rounded p-3">
                  <div className="text-muted small">RA Bill Number</div>
                  <div className="fw-bold h6 mb-0">{raBill.raBillNumber}</div>
                </div>
              </Col>
              <Col xs={24} lg={8}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Work Order</div>
                  <div className="fw-bold h6 mb-0">
                    {raBill.workOrderNumber}
                  </div>
                </div>
              </Col>
              <Col xs={24} lg={8}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Contractor</div>
                  <div className="fw-bold h6 mb-0">{raBill.contractor}</div>
                </div>
              </Col>
              <Col xs={24} lg={8}>
                <div className="border rounded p-3">
                  <div className="text-muted small">RA Period</div>
                  <div className="fw-bold h6 mb-0">
                    {dayjs(raBill.raPeriod.from).format("DD-MMM")} to{" "}
                    {dayjs(raBill.raPeriod.to).format("DD-MMM-YYYY")}
                  </div>
                </div>
              </Col>
              <Col xs={24} lg={8}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Site</div>
                  <div className="fw-bold h6 mb-0">{raBill.site}</div>
                </div>
              </Col>
              <Col xs={24} lg={8}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Status</div>
                  <Tag color={getStatusColor(raBill.status)} className="mt-1">
                    {raBill.status}
                  </Tag>
                </div>
              </Col>
            </Row>

            {/* Commercial Summary */}
            <Divider>Commercial Summary</Divider>
            <Row gutter={[24, 16]}>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">WO Total Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(raBill.woTotalValue,"INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Done</div>
                  <div className="fw-bold h5 mb-0 text-success">
                    {raBill.cumulativeWorkDone}%
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(raBill.cumulativeWorkValue,"INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Previous RA Bills</div>
                  <div className="fw-bold h5 mb-0 text-info">
                    {formatCurrency(raBill.previousRABillsValue,"INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Current Bill Value</div>
                  <div className="fw-bold h5 mb-0 text-primary">
                    {formatCurrency(raBill.currentBillValue,"INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Balance Works</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(raBill.balanceWorksForRABill,"INR")}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Work Lines */}
          <Card title="Work Lines Details" className="mb-4">
            <Table
              columns={workLinesColumns}
              dataSource={raBillLines}
              rowKey="raBillLineId"
              scroll={{ x: 1600 }}
              pagination={false}
              size="small"
            />
          </Card>

          {/* Deductions */}
          <Card title="Deductions & Recoveries" className="mb-4">
            <Row gutter={[24, 16]}>
              {deductions.map((deduction) => (
                <Col xs={24} lg={6} key={deduction.id}>
                  <div className="border rounded p-3">
                    <div className="fw-bold mb-2">{deduction.type}</div>
                    {deduction.percentage && (
                      <div className="text-muted small mb-2">
                        @ {deduction.percentage}%
                      </div>
                    )}
                    <div className="fw-bold text-danger">
                      {formatCurrency(deduction.amount,"INR")}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Totals */}
          {totals && (
            <Card title="Bill Totals" className="mb-4">
              <Row gutter={[24, 16]} className="text-center">
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Subtotal (excl. tax)</div>
                    <div className="fw-bold h5 mb-0">
                      {formatCurrency(totals.subtotalExclTax,"INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">GST Total</div>
                    <div className="fw-bold h5 mb-0 text-warning">
                      {formatCurrency(totals.gstTotal,"INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Gross Total</div>
                    <div className="fw-bold h5 mb-0">
                      {formatCurrency(totals.grossTotal,"INR")}
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Total Deductions</div>
                    <div className="fw-bold h5 mb-0 text-danger">
                      -{formatCurrency(totals.totalDeductions,"INR")}
                    </div>
                  </div>
                </Col>
              </Row>
              <Divider />
              <div className="text-center">
                <div className="display-6 fw-bold text-success">
                  Net Payable: {formatCurrency(totals.netPayable,"INR")}
                </div>
              </div>
            </Card>
          )}

          {/* Payment Information (for approved bills) */}
          {mode === "view" && raBill.status !== "Draft" && (
            <Card title="Payment Information" className="mb-4">
              <Row gutter={[24, 16]}>
                <Col xs={24} lg={12}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Payment Status</div>
                    <Tag
                      color={
                        raBill.paymentStatus === "Paid"
                          ? "success"
                          : raBill.paymentStatus === "Partially Paid"
                          ? "warning"
                          : "error"
                      }
                    >
                      {raBill.paymentStatus}
                    </Tag>
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <div className="border rounded p-3">
                    <div className="text-muted small">Amount Paid</div>
                    <div className="fw-bold h5 mb-0 text-success">
                      {formatCurrency(raBill.paidAmount,"INR")}
                    </div>
                  </div>
                </Col>
                {raBill.lastPaymentDate && (
                  <>
                    <Col xs={24} lg={12}>
                      <div className="border rounded p-3">
                        <div className="text-muted small">
                          Last Payment Date
                        </div>
                        <div className="fw-bold h6 mb-0">
                          {dayjs(raBill.lastPaymentDate).format("DD-MMM-YYYY")}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} lg={12}>
                      <div className="border rounded p-3">
                        <div className="text-muted small">Payment Mode</div>
                        <div className="fw-bold h6 mb-0">
                          {raBill.paymentMode}
                        </div>
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </Card>
          )}

          {/* Audit Trail */}
          <Card title="Audit Trail">
            <Timeline>
              <Timeline.Item
                dot={<UserOutlined className="text-primary" />}
                color="blue"
              >
                <div>
                  <strong>Created</strong> by {raBill.createdBy}
                  <div className="text-muted small">
                    {dayjs(raBill.createdOn).format("DD-MMM-YYYY HH:mm")}
                  </div>
                </div>
              </Timeline.Item>

              {raBill.submittedOn && (
                <Timeline.Item
                  dot={<CalendarOutlined className="text-warning" />}
                  color="orange"
                >
                  <div>
                    <strong>Submitted</strong> by {raBill.submittedBy}
                    <div className="text-muted small">
                      {dayjs(raBill.submittedOn).format("DD-MMM-YYYY HH:mm")}
                    </div>
                  </div>
                </Timeline.Item>
              )}

              {raBill.approvedOn && (
                <Timeline.Item
                  dot={<CheckCircleOutlined className="text-success" />}
                  color="green"
                >
                  <div>
                    <strong>Approved</strong> by {raBill.approvedBy}
                    <div className="text-muted small">
                      {dayjs(raBill.approvedOn).format("DD-MMM-YYYY HH:mm")}
                    </div>
                  </div>
                </Timeline.Item>
              )}
            </Timeline>
          </Card>
        </div>
      </div>

      {/* Action Modal */}
      <Modal
        title={actionType === "approve" ? "Approve RA Bill" : "Return RA Bill"}
        open={actionModalVisible}
        onCancel={() => setActionModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleActionSubmit} layout="vertical">
          <div className="mb-3">
            <Alert
              type={actionType === "approve" ? "success" : "warning"}
              message={
                actionType === "approve"
                  ? `You are about to approve RA Bill ${raBill.raBillNumber}`
                  : `You are about to return RA Bill ${raBill.raBillNumber} to contractor`
              }
              description={
                actionType === "approve"
                  ? "Once approved, this RA Bill will move to the next stage in the workflow."
                  : "Please provide comments explaining why this RA Bill is being returned."
              }
              showIcon
            />
          </div>

          <Form.Item
            label="Comments"
            name="comments"
            rules={
              actionType === "return"
                ? [
                    {
                      required: true,
                      message: "Comments are required when returning",
                    },
                  ]
                : []
            }
          >
            <TextArea
              rows={4}
              placeholder={`Enter your comments for ${
                actionType === "approve" ? "approval" : "return"
              }...`}
            />
          </Form.Item>

          <div className="text-end">
            <Space>
              <Button onClick={() => setActionModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                danger={actionType === "return"}
              >
                {actionType === "approve"
                  ? "Approve RA Bill"
                  : "Return to Contractor"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </Content>
  );
};

export default RABillDetails;
